import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, skip, take } from 'rxjs/operators';
import { fromEvent, interval, merge } from 'rxjs';
import { OfflineStorageService, OfflineOperation } from './offline-storage.service';
import { OrderService } from 'src/app/services/pedidos/pedidos.service';
import { ViajesFirmaService } from 'src/app/services/firma-digital/viajes-firma.service';
import { OrdersListRefreshService } from './orders-list-refresh.service';
import { EventLogService } from 'src/app/shared/logging/event-log.service';
import { StoreForwardMonitoringService } from 'src/core/logging/store-forward-monitoring.service';
import { mapOfflineOpToEndpointDestino } from 'src/core/logging/store-forward.mapper';
import {
  httpErrorContext,
  httpSuccessContext,
  HttpEventLogContext,
  mergeHttpLogPayload,
} from 'src/core/logging/http-event-log-context';
import { randomUuid } from 'src/core/logging/uuid.util';
import {
  isAlreadyProcessedError,
  parseViajesFirmaErrorMessage,
} from 'src/app/services/pedidos/viajes-firma.mapper';

@Injectable({ providedIn: 'root' })
export class SyncService {

  private isFlushing = false;
  // Set when something requests a flush while one is already running. Cleared by the
  // finally-block, which then re-triggers — closes the race where an op is queued
  // between getQueue() snapshot and removeQueueEntry().
  private pendingFlush = false;

  private readonly RETRY_INTERVAL_MS    = 15_000;  // periodic safety-net retry while online
  private readonly OPERATION_TIMEOUT_MS = 30_000;  // any single op hanging longer is treated as failed
  // Backup threshold: 30 min of *online* failure (not wall-clock since queued). An op
  // that was queued offline for hours and then syncs in 2s after reconnect produces
  // no file. Only ops that actively fail to send despite the device being online for
  // half an hour get backed up. Tunable here in one place.
  private readonly STUCK_BACKUP_AGE_MS  = 30 * 60_000;

  /** Fallos recientes por tkt+tipo para waitForOpSent. */
  private readonly recentOpFailures = new Map<string, number>();
  private readonly recentOpFailureDetails = new Map<
    string,
    { message: string; terminal: boolean }
  >();

  /** operationIds ya notificados al API de monitoreo en esta sesión. */
  private readonly registeredMonitoringOps = new Set<string>();

  private flushingSubject = new BehaviorSubject<boolean>(false);
  readonly flushing$: Observable<boolean> = this.flushingSubject.asObservable();

  constructor(
    private offlineStorage: OfflineStorageService,
    private orderService: OrderService,
    private readonly viajesFirmaService: ViajesFirmaService,
    private readonly ordersListRefresh: OrdersListRefreshService,
    private readonly eventLog: EventLogService,
    private readonly storeForwardMonitoring: StoreForwardMonitoringService
  ) {
    // Offline → online transition
    this.offlineStorage.online$
      .pipe(skip(1), filter(online => online))
      .subscribe(() => this.triggerFlush());

    // New op queued while online
    this.offlineStorage.operationQueued$
      .pipe(filter(() => this.offlineStorage.isOnline()))
      .subscribe(() => this.triggerFlush());

    this.offlineStorage.operationQueued$.subscribe(() => {
      void this.logOperationQueued();
    });

    // Periodic retry while online. 15s strikes a balance between responsiveness for
    // the driver waiting for a sign to sync and server load. The per-op timeout means
    // this interval will always find a healthy isFlushing=false to work against.
    interval(this.RETRY_INTERVAL_MS)
      .pipe(filter(() => this.offlineStorage.isOnline()))
      .subscribe(() => this.triggerFlush());

    // User-activity / app-resume signals. The Android WebView can throttle the RxJS
    // interval when backgrounded, so visibilitychange+focus are the reliable wake-up
    // triggers. Cheap and idempotent thanks to isFlushing/pendingFlush dedup.
    merge(
      fromEvent(window, 'focus'),
      fromEvent(document, 'visibilitychange').pipe(
        filter(() => document.visibilityState === 'visible')
      ),
    ).subscribe(() => this.triggerFlush());
  }

  /** Call once on app init to flush any items left from a previous session. */
  init(): void {
    this.triggerFlush();
  }

  /** Public trigger — used by components (QR screen, Actualizar button, navigation). */
  flushNow(): void {
    this.triggerFlush();
  }

  /** Flush manual con espera — botón Reenviar pendientes (5.4). */
  async flushNowAndWait(): Promise<void> {
    this.flushNow();
    await this.waitForFlushIdle(60_000);
  }

  /**
   * Espera a que una operacion de cola se confirme enviada (ausente en cola).
   * Usado por firma canvas online antes de mostrar exito al usuario.
   */
  async waitForOpSent(
    tkt: string,
    type: 'signedCanvas',
    timeoutMs = 30_000
  ): Promise<'sent' | 'timeout' | 'failed'> {
    const watchStarted = Date.now();
    const failureKey = `${tkt}:${type}`;

    while (Date.now() - watchStarted < timeoutMs) {
      const failedAt = this.recentOpFailures.get(failureKey);
      if (failedAt !== undefined && failedAt >= watchStarted) {
        return 'failed';
      }

      const queue = await this.offlineStorage.getQueue();
      const entry = queue.find((e) => e.tkt === tkt);
      const stillPending = entry?.operations.some((op) => op.type === type) ?? false;
      if (!stillPending) {
        return 'sent';
      }

      this.flushNow();
      await this.waitForFlushIdle(Math.min(5_000, timeoutMs));
      await this.delay(300);
    }

    return 'timeout';
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private async waitForFlushIdle(maxMs: number): Promise<void> {
    const start = Date.now();
    while (this.isFlushing && Date.now() - start < maxMs) {
      await this.delay(50);
    }
  }

  private markOpFailure(
    tkt: string,
    type: OfflineOperation['type'],
    message?: string,
    terminal = false
  ): void {
    const key = `${tkt}:${type}`;
    this.recentOpFailures.set(key, Date.now());
    if (message) {
      this.recentOpFailureDetails.set(key, { message, terminal });
    }
  }

  consumeLastOpFailure(
    tkt: string,
    type: OfflineOperation['type']
  ): { message: string; terminal: boolean } | null {
    return this.recentOpFailureDetails.get(`${tkt}:${type}`) ?? null;
  }

  // ─── Flush ─────────────────────────────────────────────────────────────────

  /**
   * Request a flush. Deduplicates concurrent calls: if a flush is already running
   * we record that another one is wanted and let the current one re-trigger at the end.
   */
  private triggerFlush(): void {
    if (!this.offlineStorage.isOnline()) return;
    if (this.isFlushing) {
      this.pendingFlush = true;
      return;
    }
    // Fire-and-forget — flushQueue handles its own errors and always releases isFlushing.
    this.flushQueue();
  }

  private async flushQueue(): Promise<void> {
    if (!this.offlineStorage.isOnline()) return;
    if (this.isFlushing) { this.pendingFlush = true; return; }

    // Don't flush if we have no auth key yet — every send would go out as `Key: undefined`,
    // get rejected, and waste a retry slot. The key is rehydrated from localStorage in
    // OrderService's constructor, so the only time this is false is when no user has ever
    // logged in on this device. Trigger a re-attempt on the next event (online, navigation,
    // operationQueued) — by then login or the auth guard will have populated the key.
    if (!this.orderService.hasAuthKey()) {
      console.warn('[SyncService] Skipping flush — auth key not loaded yet');
      return;
    }

    this.isFlushing = true;
    this.flushingSubject.next(true);
    let stats = { sent: 0, failed: 0, mismatchedUser: 0 };
    try {
      const queue = await this.offlineStorage.getQueue();
      const currentUserRut = this.getCurrentUserRut();

      for (const entry of queue) {
        if (!this.offlineStorage.isOnline()) break;

        for (const op of entry.operations) {
          if (op.status === 'paused' || op.status === 'blocked') {
            // Pausada o rechazada por el servidor: no reintentar ni bloquear el resto del ticket.
            continue;
          }

          const opUserRut = op.userRut !== undefined && op.userRut !== null
            ? String(op.userRut).trim()
            : '';
          if (opUserRut && currentUserRut && opUserRut !== currentUserRut) {
            console.warn(
              `[SyncService] Skipping op "${op.type}" for tkt ${entry.tkt}: ` +
              `queued by "${opUserRut}", current user is "${currentUserRut}". ` +
              `Will retry when that user logs back in.`
            );
            await this.offlineStorage.markOperationBlocked(
              entry.tkt,
              op.queuedAt,
              `Pendiente de envío con el usuario ${opUserRut}. Inicie sesión con ese usuario para reenviar.`,
            ).catch(() => null);
            await this.logStoreForward(
              'error_permanente',
              {
                tkt: entry.tkt,
                type: op.type,
                reason: 'user_mismatch',
                operationId: this.resolveOperationId(entry.tkt, op),
                correlationId: op.correlationId,
              },
              {
                endpointOrigen: mapOfflineOpToEndpointDestino(op),
                codigoError: 'USER_MISMATCH',
              }
            );
            this.storeForwardMonitoring.updateOperationState(
              op,
              this.resolveOperationId(entry.tkt, op),
              'ERROR_PERMANENTE',
              {
                ultimoMensaje: 'Usuario de cola distinto al activo.',
                ultimoCodigoError: 'USER_MISMATCH',
              }
            );
            stats.mismatchedUser++;
            break;
          }

          if (op.type === 'signedCanvas' && !this.viajesFirmaService.hasGatewayToken()) {
            console.warn(
              `[SyncService] Skipping signedCanvas for tkt ${entry.tkt}: no Bearer token`
            );
            await this.offlineStorage.markOperationBlocked(
              entry.tkt,
              op.queuedAt,
              'Sesión sin token Bearer — el envío se reintentará al iniciar sesión.',
            ).catch(() => null);
            break;
          }

          if (!op.operationId) {
            console.warn('[SyncService] Operation without operationId after queue normalization', op);
            break;
          }

          const claimed = await this.offlineStorage
            .tryMarkOperationAttempt(entry.tkt, op.operationId)
            .catch(() => false);
          if (!claimed) {
            // La operacion pudo pausarse o cambiar de estado despues del snapshot.
            continue;
          }

          const operationId = this.resolveOperationId(entry.tkt, op);
          const attemptId = this.storeForwardMonitoring.createAttemptId();
          const numeroIntento = (op.retryCount ?? 0) + 1;
          const attemptStartedAt = Date.now();

          this.storeForwardMonitoring.updateOperationState(op, operationId, 'REINTENTANDO');
          await this.logStoreForward('reintento_iniciado', {
            tkt: entry.tkt,
            type: op.type,
            operationId,
            correlationId: op.correlationId,
            attemptId,
          });

          try {
            await this.withTimeout(
              this.processOperation(op),
              this.OPERATION_TIMEOUT_MS,
              op.type,
            );
            this.storeForwardMonitoring.registerAttempt(
              op,
              operationId,
              attemptId,
              numeroIntento,
              'EXITOSO',
              { duracionMs: Date.now() - attemptStartedAt }
            );
            await this.completeOperationSuccess(entry.tkt, op, operationId);
            stats.sent++;
          } catch (err) {
            const errBody =
              err && typeof err === 'object' && 'error' in err
                ? (err as { error?: unknown }).error
                : err;
            const httpStatus =
              err && typeof err === 'object' && 'status' in err
                ? (err as { status?: number }).status
                : undefined;

            if (this.isIdempotentAlreadyProcessed(op, errBody, httpStatus)) {
              console.warn(
                `[SyncService] Op "${op.type}" for tkt ${entry.tkt} already processed on server — removing from queue.`
              );
              this.storeForwardMonitoring.registerAttempt(
                op,
                operationId,
                attemptId,
                numeroIntento,
                'EXITOSO',
                {
                  duracionMs: Date.now() - attemptStartedAt,
                  mensaje: 'Ya procesado en servidor (idempotente).',
                  codigoHttp: httpStatus,
                }
              );
              await this.completeOperationSuccess(entry.tkt, op, operationId);
              stats.sent++;
              continue;
            }

            const errorMessage = this.extractErrorMessage(err, errBody, op.type, httpStatus);
            const isPermanent = this.isPermanentFailure(httpStatus, errBody, op.type);
            this.storeForwardMonitoring.registerAttempt(
              op,
              operationId,
              attemptId,
              numeroIntento,
              isPermanent ? 'ERROR_PERMANENTE' : 'ERROR_TEMPORAL',
              {
                duracionMs: Date.now() - attemptStartedAt,
                codigoHttp: httpStatus,
                codigoError:
                  errBody &&
                  typeof errBody === 'object' &&
                  typeof (errBody as { code?: unknown }).code === 'string'
                    ? String((errBody as { code: string }).code)
                    : undefined,
                mensaje: errorMessage,
                detalleTecnico: typeof errBody === 'string' ? errBody : undefined,
              }
            );
            if (isPermanent) {
              this.storeForwardMonitoring.updateOperationState(op, operationId, 'ERROR_PERMANENTE', {
                ultimoCodigoHttp: httpStatus,
                ultimoMensaje: errorMessage,
              });
              const endpointOrigen = mapOfflineOpToEndpointDestino(op);
              const httpCtx =
                err instanceof HttpErrorResponse
                  ? httpErrorContext(err, endpointOrigen)
                  : {
                      endpointOrigen,
                      codigoHttp: httpStatus,
                      codigoError:
                        errBody &&
                        typeof errBody === 'object' &&
                        typeof (errBody as { code?: unknown }).code === 'string'
                          ? String((errBody as { code: string }).code)
                          : undefined,
                      message: errorMessage,
                    };
              await this.logStoreForward(
                'error_permanente',
                {
                  tkt: entry.tkt,
                  type: op.type,
                  operationId,
                  correlationId: op.correlationId,
                },
                httpCtx
              );
            }
            console.error(
              `[SyncService] Failed to process operation "${op.type}" for tkt ${entry.tkt}` +
                (httpStatus !== undefined ? ` (HTTP ${httpStatus})` : '') +
                ':',
              errBody ?? err
            );
            this.markOpFailure(entry.tkt, op.type, errorMessage, isPermanent);
            stats.failed++;
            const persistFailure = isPermanent
              ? this.offlineStorage.markOperationBlocked(entry.tkt, op.queuedAt, errorMessage)
              : this.offlineStorage.markOperationFailed(entry.tkt, op.queuedAt, errorMessage);
            await persistFailure.catch(() => null);
            break;
          }
        }
      }
      // Single-line summary so we can tell at a glance whether items are getting
      // stuck because of network errors vs user-mismatch (the most common culprit
      // for "queue count never decreases").
      // if (stats.sent || stats.failed || stats.mismatchedUser) {
      //   console.log(
      //     `[SyncService] flush summary — sent: ${stats.sent}, failed: ${stats.failed}, ` +
      //     `skipped (user mismatch): ${stats.mismatchedUser}`
      //   );
      // }

      if (stats.sent > 0) {
        this.ordersListRefresh.requestListRefresh();
      }

      // Lazy backup pass: any signature-bearing op still in the queue >5 min after
      // queueing AND not yet backed up gets a JSON+PNG written to Downloads. This
      // is the second backup trigger (the first is in addOperationToQueue when
      // queueing offline). Keeps Downloads clean for the common case where ops
      // sync within seconds, while still protecting drivers whose tablets ride out
      // long network failures.
      await this.backupStuckOps();
    } finally {
      this.isFlushing = false;
      this.flushingSubject.next(false);
      if (this.pendingFlush) {
        this.pendingFlush = false;
        // Small delay so persistent server errors don't tight-loop.
        setTimeout(() => this.triggerFlush(), 500);
      }
    }
  }

  /**
   * Walks the queue looking for signature-bearing ops that have been failing on a
   * healthy online connection for STUCK_BACKUP_AGE_MS, and writes a JSON+PNG snapshot
   * of each to public Downloads. Marks them backedUp so we never duplicate.
   *
   * The timer uses `failingSince` (stamped on first online flush failure) rather than
   * `queuedAt`. That means:
   *   - Op queued offline for hours, syncs in 2s after reconnect → never stamped
   *     failingSince → never backed up.
   *   - Op queued, fails on first send, stays failing for 30 min → backed up.
   *   - Op queued, brief network hiccup causes one failure, then succeeds → removed
   *     from queue before this pass ever sees it → never backed up.
   *
   * Runs once per flush after the normal send loop; safe to call repeatedly because
   * `backedUp` gates the actual write inside backupOperation.
   */
  private async backupStuckOps(): Promise<void> {
    try {
      const queue = await this.offlineStorage.getQueue();
      const now = Date.now();
      for (const entry of queue) {
        for (const op of entry.operations) {
          if (op.backedUp) continue;
          if (op.failingSince === undefined) continue;
          if (now - op.failingSince < this.STUCK_BACKUP_AGE_MS) continue;
          // backupOperation handles the shouldBackup() filter and the markOperationBackedUp call.
          await this.offlineStorage.backupOperation(entry.tkt, op).catch(() => null);
        }
      }
    } catch (e) {
      console.warn('[SyncService] backupStuckOps failed:', e);
    }
  }

  /**
   * Returns the active user's RUT as a normalized (trimmed) string. Must match how
   * OfflineStorageService stamps queued ops, otherwise userRut comparisons short-circuit
   * to a false mismatch and silently strand the entire queue.
   */
  private getCurrentUserRut(): string {
    try {
      const user = JSON.parse(localStorage.getItem('user') ?? 'null');
      const raw = user?.RUT_USUARIO;
      if (raw === null || raw === undefined) return '';
      return String(raw).trim();
    } catch {
      return '';
    }
  }

  /**
   * Rejects if `p` doesn't settle within `ms`. This is the safety valve that prevents
   * a hung fetch (captive portal, TCP accepted but no response, DNS stall) from
   * permanently deadlocking isFlushing — without it, a single bad network moment can
   * leave the queue unable to flush until the app is killed.
   */
  private withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const t = setTimeout(
        () => reject(new Error(`[SyncService] operation "${label}" timed out after ${ms}ms`)),
        ms,
      );
      p.then(
        v => { clearTimeout(t); resolve(v); },
        e => { clearTimeout(t); reject(e); },
      );
    });
  }

  // ─── Operation dispatch ────────────────────────────────────────────────────

  private async processOperation(op: OfflineOperation): Promise<void> {
    switch (op.type) {

      case 'statusUpdate':
        // Use sendStatusUpdate (direct HTTP) — updateStatus() would re-queue
        await this.orderService.sendStatusUpdate(
          op.payload.tkt,
          op.payload.orderNumber,
          op.payload.status,
          op.payload.capturedHour,
        ).pipe(take(1)).toPromise();
        break;

      case 'extraValues': {
        const files = op.payload.files.map(f => this.base64ToFile(f.base64, f.name));
        await this.orderService.sendExtraValues(
          op.payload.tkt,
          op.payload.orderNumber,
          op.payload.waterAddition,
          op.payload.anotherAddition,
          op.payload.guidingRemarks,
          op.payload.m3Returned,
          op.payload.reasonToReturn,
          op.payload.m3ReturnedPallet,
          files,
          op.payload.files,
        ).pipe(take(1)).toPromise();
        break;
      }

      case 'extraField':
        await this.orderService.sendExtraField(
          op.payload.tkt,
          op.payload.orderNumber,
          op.payload.field,
          op.payload.value,
        ).pipe(take(1)).toPromise();
        break;

      case 'unsigned':
        await this.orderService.sendUnsignedGuide(
          op.payload.tkt, op.payload.orderNumber
        ).pipe(take(1)).toPromise();
        break;

      case 'clearPending':
        await this.orderService.sendClearPendingOrder(
          op.payload.tkt,
          op.payload.orderNumber,
          {
            commentsFinal: op.payload.commentsFinal,
            finFlujo: op.payload.finFlujo,
          }
        ).pipe(take(1)).toPromise();
        break;

      case 'signed':
        await this.orderService.sendSignedGuide(
          op.payload.tkt,
          op.payload.orderNumber,
          op.payload.rut,
          op.payload.name,
          op.payload.signature,
          op.payload.email,
        ).pipe(take(1)).toPromise();
        break;

      case 'signedCanvas':
        await this.viajesFirmaService.sendSignedGuideCanvas(
          op.payload.tkt,
          op.payload.orderNumber,
          op.payload.rut,
          op.payload.name,
          op.payload.signature,
          op.payload.email,
          op.payload.tipoFirma,
          op.payload.comentario,
          op.payload.emailDos,
        ).pipe(take(1)).toPromise();
        break;

      case 'rejected': {
        const files = op.payload.files.map(f => this.base64ToFile(f.base64, f.name));
        await this.orderService.sendRejectValues(
          op.payload.tkt,
          op.payload.orderNumber,
          op.payload.rejectReason,
          op.payload.rejectDescription,
          files,
          op.payload.isMixer,
          op.payload.rutClient,
          op.payload.files,
        ).pipe(take(1)).toPromise();
        break;
      }
    }
  }

  // ─── Helpers ───────────────────────────────────────────────────────────────

  private async completeOperationSuccess(
    tkt: string,
    op: OfflineOperation,
    operationId: string
  ): Promise<void> {
    this.storeForwardMonitoring.updateOperationState(op, operationId, 'ENVIADO', {
      ultimoMensaje: 'Operación sincronizada correctamente.',
    });
    await this.logStoreForward(
      'operacion_enviada',
      {
        tkt,
        type: op.type,
        operationId,
        correlationId: op.correlationId,
      },
      httpSuccessContext(mapOfflineOpToEndpointDestino(op))
    );
    await this.offlineStorage.removeFirstOperation(tkt);
    if (
      op.type === 'signed' ||
      op.type === 'signedCanvas' ||
      op.type === 'unsigned' ||
      op.type === 'clearPending' ||
      op.type === 'rejected'
    ) {
      const payload = op.payload as { tkt?: string };
      await this.offlineStorage.clearTktCache(payload.tkt ?? tkt).catch((e) =>
        console.warn('[SyncService] clearTktCache failed for tkt', tkt, e)
      );
    }
  }

  private isIdempotentAlreadyProcessed(
    op: OfflineOperation,
    errBody: unknown,
    httpStatus?: number,
  ): boolean {
    const terminal =
      op.type === 'signedCanvas' ||
      op.type === 'signed' ||
      op.type === 'unsigned' ||
      op.type === 'clearPending' ||
      op.type === 'rejected';
    if (!terminal) {
      return false;
    }
    return isAlreadyProcessedError(errBody, httpStatus);
  }

  private extractErrorMessage(
    err: unknown,
    errBody: unknown,
    opType: OfflineOperation['type'],
    httpStatus?: number,
  ): string {
    if (opType === 'signedCanvas') {
      const fromApi = parseViajesFirmaErrorMessage(errBody);
      if (fromApi !== 'La firma no pudo ser almacenada.') {
        return fromApi;
      }
    }
    if (err && typeof err === 'object' && 'message' in err) {
      const msg = (err as { message?: string }).message;
      if (msg?.trim()) {
        return msg;
      }
    }
    if (typeof errBody === 'string' && errBody.trim()) {
      return errBody;
    }
    if (httpStatus !== undefined) {
      return `Error HTTP ${httpStatus}`;
    }
    return 'Error de envío — se reintentará automáticamente.';
  }

  private async enrichGuiaPayload(
    payload: Record<string, unknown>
  ): Promise<Record<string, unknown>> {
    const tkt = payload['tkt'];
    if (tkt == null || payload['folioNumberGD'] != null) {
      return payload;
    }
    const folioNumberGD = await this.offlineStorage.lookupGuiaNumber(String(tkt));
    return folioNumberGD != null ? { ...payload, folioNumberGD } : payload;
  }

  private async logStoreForward(
    action: string,
    payload: Record<string, unknown>,
    httpContext?: HttpEventLogContext
  ): Promise<void> {
    const enriched = await this.enrichGuiaPayload(payload);
    this.eventLog.log(
      'store-forward',
      action,
      httpContext ? mergeHttpLogPayload(enriched, httpContext) : enriched
    );
  }

  private resolveOperationId(tkt: string, op: OfflineOperation): string {
    return op.operationId ?? randomUuid();
  }

  private isPermanentFailure(
    httpStatus: number | undefined,
    errBody: unknown,
    opType: OfflineOperation['type']
  ): boolean {
    if (httpStatus === 401 || httpStatus === 403) {
      return true;
    }
    if (httpStatus === 400 || httpStatus === 422) {
      return true;
    }
    if (
      (opType === 'signedCanvas' || opType === 'signed') &&
      isAlreadyProcessedError(errBody, httpStatus)
    ) {
      return false;
    }
    return false;
  }

  private async logOperationQueued(): Promise<void> {
    try {
      const queue = await this.offlineStorage.getQueue();
      const now = Date.now();
      for (const entry of queue) {
        const last = entry.operations[entry.operations.length - 1];
        if (!last?.queuedAt || now - last.queuedAt > 3_000) {
          continue;
        }
        const operationId = this.resolveOperationId(entry.tkt, last);
        if (!this.registeredMonitoringOps.has(operationId)) {
          this.storeForwardMonitoring.registerPendingOperation(last, operationId);
          this.registeredMonitoringOps.add(operationId);
        }
        await this.logStoreForward('operacion_guardada', {
          tkt: entry.tkt,
          type: last.type,
          operationId,
          correlationId: last.correlationId,
        });
      }
    } catch {
      /* ignore */
    }
  }

  private base64ToFile(base64: string, name: string): File {
    const [header, data] = base64.split(',');
    const mime = header.match(/:(.*?);/)?.[1] ?? 'image/jpeg';
    const byteString = atob(data);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new File([ab], name, { type: mime });
  }
}
